import { ChangeDetectionStrategy, Component, inject, output } from '@angular/core';
import type { CmdkItem, CmdkPersonItem } from '../models/cmdk.model';
import { CmdkSearchService } from '../services/cmdk-search.service';
import { CmdkPreviewCommandComponent } from './cmdk-preview-command.component';
import { CmdkPreviewEmptyComponent } from './cmdk-preview-empty.component';
import { CmdkPreviewNewsComponent } from './cmdk-preview-news.component';
import { CmdkPreviewPersonComponent } from './cmdk-preview-person.component';
import { CmdkPreviewSpaceComponent } from './cmdk-preview-space.component';
import { CmdkPreviewAppComponent } from './cmdk-preview-app.component';
import { CmdkAskPaneComponent } from './cmdk-ask-pane.component';

/**
 * The right-hand column: a live preview of whatever is selected.
 *
 * A `@switch` on the selection's kind and nothing else. Each kind owns its own
 * preview component, so adding a kind is one more `@case` and one more file —
 * and, critically, the pane never has to know what a space or a person is.
 *
 * In slice 7 this same pane also hosts the Ask answer. That is the whole
 * argument of the design: the answer renders where previews render, so the
 * results list stays lit and arrow-navigable beside it instead of being
 * replaced by a conversation.
 */
@Component({
  selector: 'im-cmdk-preview-pane',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CmdkPreviewCommandComponent, CmdkPreviewEmptyComponent, CmdkPreviewAppComponent,
    CmdkAskPaneComponent, CmdkPreviewSpaceComponent, CmdkPreviewPersonComponent,
    CmdkPreviewNewsComponent,
  ],
  templateUrl: './cmdk-preview-pane.component.html',
  styleUrl: './cmdk-preview-pane.component.scss',
})
export class CmdkPreviewPaneComponent {
  private readonly search = inject(CmdkSearchService);

  readonly activated = output<CmdkItem>();
  /** Call on a colleague's preview (Flow Live). */
  readonly callPerson = output<CmdkPersonItem>();
  readonly continueInChat = output<void>();
  /** The Ask pane's close control: the palette does what Esc does. */
  readonly closeAnswer = output<void>();
  /** An Ask widget asked to show a space. */
  readonly viewSpace = output<string>();

  protected readonly selected = this.search.selectedItem;

  /** Narrow the union for the template — Angular cannot do it inline. */
  protected asCommandItem(item: CmdkItem): Extract<CmdkItem, { kind: 'nav' | 'action' }> {
    return item as Extract<CmdkItem, { kind: 'nav' | 'action' }>;
  }

  protected asAppItem(item: CmdkItem): Extract<CmdkItem, { kind: 'app' }> {
    return item as Extract<CmdkItem, { kind: 'app' }>;
  }

  protected asSpaceItem(item: CmdkItem): Extract<CmdkItem, { kind: 'space' }> {
    return item as Extract<CmdkItem, { kind: 'space' }>;
  }

  protected asPersonItem(item: CmdkItem): Extract<CmdkItem, { kind: 'person' }> {
    return item as Extract<CmdkItem, { kind: 'person' }>;
  }

  protected asNewsItem(item: CmdkItem): Extract<CmdkItem, { kind: 'news' }> {
    return item as Extract<CmdkItem, { kind: 'news' }>;
  }
}
